<script>
  import { onMount } from 'svelte';
  import { matchSetup, currentScreen, activeMatch, peerRole, deviceCheckOpen, deviceCheckReport } from '../store.js';
  import { FFPL_PLAYER_POOL } from '../../teams-data.js';
  import { createMatch, setOpeners } from '../../scoring-logic.js';
  import { persist } from '../../store.js';
  import { PeerSync } from '../../peer-sync.js';
  import { applyDeviceSession } from '../../device-session.js';
  import { saveDeviceSession } from '../../session-storage.js';
  import { isDeviceCheckFresh } from '../../media/device-check-service.js';
  import { searchPlayerInCloud, updateTeamLastXI } from '../../services/cloud-sync.js';
  import { toast } from '../../ui/dialogs.js';
  import DeviceCheckModal from '../components/DeviceCheckModal.svelte';
  import Icon from '../components/Icon.svelte';

  // Make sure we have initialized state if navigated here directly (should not happen)
  if (!$matchSetup || !$matchSetup.teamA || !$matchSetup.teamB) {
    currentScreen.set('setup');
  }

  // Prefill each team's roster with the XI used in its previous match — a
  // weekly league scorer shouldn't retype ~14 names every game. Scheduled
  // matches inject playersA/playersB via matchSetup and skip this entirely.
  function lastXIKey(teamName) { return 'cs_lastXI_' + teamName; }
  function loadLastXI(teamName, captain) {
    try {
      const custom = JSON.parse(localStorage.getItem('cs_custom_teams_v1') || "[]");
      const found = custom.find(t => t.name === teamName);
      if (found && Array.isArray(found.players) && found.players.filter(p => p.trim()).length >= 1) {
        const roster = found.players.filter(p => typeof p === 'string' && p.trim());
        while (roster.length < 8) roster.push('');
        return roster;
      }
    } catch (_) {}

    try {
      const saved = JSON.parse(localStorage.getItem(lastXIKey(teamName)) || 'null');
      if (Array.isArray(saved) && saved.filter(p => typeof p === 'string' && p.trim()).length >= 2) {
        const roster = saved.filter(p => typeof p === 'string');
        while (roster.length < 8) roster.push('');
        return roster;
      }
    } catch (_) {}
    const blank = new Array(8).fill('');
    blank[0] = captain;
    return blank;
  }

  let prefilledA = false;
  let prefilledB = false;
  if (!$matchSetup.playersA) {
    $matchSetup.playersA = loadLastXI($matchSetup.teamA.name, $matchSetup.teamA.captain);
    prefilledA = $matchSetup.playersA.filter(p => p.trim()).length > 1;
  }
  if (!$matchSetup.playersB) {
    $matchSetup.playersB = loadLastXI($matchSetup.teamB.name, $matchSetup.teamB.captain);
    prefilledB = $matchSetup.playersB.filter(p => p.trim()).length > 1;
  }

  function clearRoster(teamSlot) {
    if (teamSlot === 'A') {
      const blank = new Array(8).fill('');
      blank[0] = $matchSetup.teamA.captain;
      $matchSetup.playersA = blank;
      prefilledA = false;
    } else {
      const blank = new Array(8).fill('');
      blank[0] = $matchSetup.teamB.captain;
      $matchSetup.playersB = blank;
      prefilledB = false;
    }
  }
  
  $matchSetup.striker = $matchSetup.striker || "";
  $matchSetup.nonStriker = $matchSetup.nonStriker || "";
  $matchSetup.bowler = $matchSetup.bowler || "";

  function goBack() {
    currentScreen.set('setup');
  }

  function addPlayer(teamSlot) {
    if (teamSlot === 'A') $matchSetup.playersA = [...$matchSetup.playersA, ""];
    if (teamSlot === 'B') $matchSetup.playersB = [...$matchSetup.playersB, ""];
  }

  function removePlayer(teamSlot) {
    if (teamSlot === 'A' && $matchSetup.playersA.length > 2) {
      $matchSetup.playersA = $matchSetup.playersA.slice(0, -1);
    }
    if (teamSlot === 'B' && $matchSetup.playersB.length > 2) {
      $matchSetup.playersB = $matchSetup.playersB.slice(0, -1);
    }
  }

  $: battingLetter = $matchSetup.tossDecision === "bat" ? $matchSetup.tossWinner : ($matchSetup.tossWinner === "A" ? "B" : "A");
  $: bowlingLetter = battingLetter === "A" ? "B" : "A";
  
  $: battingTeam = battingLetter === "A" ? $matchSetup.teamA : $matchSetup.teamB;
  $: bowlingTeam = bowlingLetter === "A" ? $matchSetup.teamA : $matchSetup.teamB;
  
  $: battingPool = (battingLetter === "A" ? $matchSetup.playersA : $matchSetup.playersB).filter(x => x.trim());
  $: bowlingPool = (bowlingLetter === "A" ? $matchSetup.playersA : $matchSetup.playersB).filter(x => x.trim());

  let showSearchModal = false;
  let searchSlot = 'A';
  let searchVal = '';
  let searchResult = null;
  let searchError = '';
  let isSearchingPlayer = false;

  function openImportSearch(slot) {
    searchSlot = slot;
    showSearchModal = true;
    searchVal = '';
    searchResult = null;
    searchError = '';
  }

  async function triggerSearch() {
    if (!searchVal.trim()) return;
    isSearchingPlayer = true;
    searchError = '';
    searchResult = null;
    try {
      const result = await searchPlayerInCloud(searchVal.trim());
      if (result) {
        searchResult = result;
      } else {
        searchError = "Player profile not found. Search by username or email.";
      }
    } catch (err) {
      searchError = "Failed to search: " + err.message;
    } finally {
      isSearchingPlayer = false;
    }
  }

  function addImportedPlayer() {
    if (!searchResult) return;
    const name = searchResult.displayName;
    const slot = searchSlot;

    try {
      const reg = JSON.parse(localStorage.getItem('cs_player_registry') || '{}');
      reg[name] = searchResult;
      localStorage.setItem('cs_player_registry', JSON.stringify(reg));
    } catch (_) {}

    const players = slot === 'A' ? $matchSetup.playersA : $matchSetup.playersB;
    const emptyIdx = players.findIndex(p => !p.trim());
    if (emptyIdx >= 0) {
      players[emptyIdx] = name;
    } else {
      if (slot === 'A') {
        $matchSetup.playersA = [...$matchSetup.playersA, name];
      } else {
        $matchSetup.playersB = [...$matchSetup.playersB, name];
      }
    }
    
    $matchSetup = $matchSetup;
    showSearchModal = false;
  }

  let phoneMode = "single_record"; // "single_record", "single_score", "scorer", or "recorder"
  let isStarting = false;
  let startError = "";
  let pendingDeviceCheckResolve = null;

  // Pre-match device safety check removed per user request — it added a
  // blocking modal before every recording match without real value. The
  // camera/mic permission prompt happens naturally when recording starts.
  onMount(() => {});

  function handlePhoneModeChange(mode) {
    phoneMode = mode;
  }

  function ensureRecordingDeviceCheck() {
    const report = $deviceCheckReport;
    if (isDeviceCheckFresh(report)) {
      if (report.status === 'ready') return Promise.resolve(true);
      if (report.status === 'warning' && report.acceptedAt) return Promise.resolve(true);
      if (report.status === 'blocked') deviceCheckOpen.set(true);
    } else {
      deviceCheckOpen.set(true);
    }
    return new Promise(resolve => {
      pendingDeviceCheckResolve = resolve;
    });
  }

  function handleDeviceCheckAccepted() {
    const resolve = pendingDeviceCheckResolve;
    pendingDeviceCheckResolve = null;
    if (resolve) resolve(true);
  }

  function handleDeviceCheckClosed() {
    const resolve = pendingDeviceCheckResolve;
    pendingDeviceCheckResolve = null;
    if (resolve) resolve(false);
  }

  async function startMatch() {
    const A = $matchSetup.playersA.filter(x => x.trim());
    const B = $matchSetup.playersB.filter(x => x.trim());
    
    const minPlayers = $matchSetup.mode === 'challenge' ? 1 : 2;
    if (A.length < minPlayers || B.length < minPlayers) { toast(`Each team needs at least ${minPlayers} player(s)`, "error"); return; }
    if (!$matchSetup.striker || !$matchSetup.bowler) { toast("Select striker and bowler", "error"); return; }
    if (minPlayers >= 2 && !$matchSetup.nonStriker) { toast("Select non-striker", "error"); return; }
    if ($matchSetup.nonStriker && $matchSetup.striker === $matchSetup.nonStriker) { toast("Striker and non-striker must differ", "error"); return; }

    // Remember each team's XI for one-tap reuse next match
    try {
      localStorage.setItem(lastXIKey($matchSetup.teamA.name), JSON.stringify(A));
      localStorage.setItem(lastXIKey($matchSetup.teamB.name), JSON.stringify(B));
    } catch (_) {}

    // Cloud teams: remember today's XI on the team doc too, so the NEXT
    // import (on anyone's phone) preselects it automatically.
    if ($matchSetup.teamA.cloudTeamId) updateTeamLastXI($matchSetup.teamA.cloudTeamId, A);
    if ($matchSetup.teamB.cloudTeamId) updateTeamLastXI($matchSetup.teamB.cloudTeamId, B);

    if (phoneMode === 'recorder') {
      currentScreen.set('recorder-join');
      return;
    }

    startError = "";
    const newMatch = createMatch({
      teamA: { name: $matchSetup.teamA.name, players: A },
      teamB: { name: $matchSetup.teamB.name, players: B },
      overs: $matchSetup.overs, mode: $matchSetup.mode, lms: $matchSetup.lms,
      toss: { winner: $matchSetup.tossWinner, choice: $matchSetup.tossDecision },
      challenge: $matchSetup.mode === 'challenge' ? $matchSetup.challenge : null,
      rules: $matchSetup.rules,
      // Tournament linkage — without these the fixture result never reaches
      // the points table (local or cloud).
      tournamentId: $matchSetup.tournamentId,
      tournamentMatchId: $matchSetup.tournamentMatchId,
      cloudTournamentId: $matchSetup.cloudTournamentId,
      cloudFixtureId: $matchSetup.cloudFixtureId
    });
    
    setOpeners(newMatch, $matchSetup.striker, $matchSetup.nonStriker, $matchSetup.bowler);
    activeMatch.set(newMatch);
    if (phoneMode === 'single_record') {
      applyDeviceSession(newMatch, phoneMode);
      saveDeviceSession({ role: phoneMode, screen: 'recording' });
      peerRole.set(null);
      currentScreen.set('recording');
    } else if (phoneMode === 'single_score') {
      applyDeviceSession(newMatch, phoneMode);
      saveDeviceSession({ role: phoneMode, screen: 'scoring' });
      peerRole.set(null);
      currentScreen.set('scoring');
    } else if (phoneMode === 'scorer') {
      isStarting = true;
      try {
        const roomCode = await PeerSync.createRoom();
        applyDeviceSession(newMatch, phoneMode, roomCode);
        saveDeviceSession({ role: phoneMode, roomCode, screen: 'scoring' });
        currentScreen.set('scoring');
      } catch (err) {
        PeerSync.disconnect();
        activeMatch.set(null);
        startError = `Could not create recorder code: ${err?.message || 'connection server unavailable'}`;
        currentScreen.set('players');
      } finally {
        isStarting = false;
      }
    }
    persist();
  }
</script>

<datalist id="player-pool">
  {#each FFPL_PLAYER_POOL as p, i_each (p.id || p.name || i_each)}
    <option value={p}></option>
  {/each}
</datalist>

<div class="screen">
  <button class="btn btn-ghost" on:click={goBack}>← Back</button>
  <h1 style="margin-top:10px">Players Setup</h1>

  <!-- TEAM A PLAYERS -->
  <div class="card">
    <div class="team-header">
      <img src={$matchSetup.teamA.logo} class="team-logo-sm" alt="logo" />
      <div class="team-name" style="color:{$matchSetup.teamA.color}">{$matchSetup.teamA.name}</div>
    </div>
    {#if prefilledA}
      <div class="muted" style="font-size:11px; margin-bottom:6px; color:#86efac">✓ Last match's XI loaded — edit as needed</div>
    {/if}
    {#each $matchSetup.playersA as p, i (i)}
      <div class="player-row">
        <div class="num" style="display:flex; align-items:center; justify-content:center;">
          {#if i === 0}<Icon name="trophy" size={12} />{:else}{i + 1}{/if}
        </div>
        <input type="text" list="player-pool" bind:value={$matchSetup.playersA[i]} placeholder={i === 0 ? "Captain" : "Player " + (i + 1)} />
      </div>
    {/each}
    <div style="display:flex; gap: 8px; margin-top: 12px; flex-wrap: wrap;">
      <button class="btn btn-ghost" on:click={() => addPlayer('A')}>+ Add Player</button>
      <button class="btn btn-ghost" disabled={$matchSetup.playersA.length <= 2} on:click={() => removePlayer('A')}>- Remove</button>
      <button class="btn btn-ghost" style="color:#fbbf24; border-color:#fbbf24; display:flex; align-items:center; gap:6px;" on:click={() => openImportSearch('A')}>
        <Icon name="user" size={14} /> Import Profile
      </button>
      {#if prefilledA}
        <button class="btn btn-ghost" on:click={() => clearRoster('A')}>✕ Clear</button>
      {/if}
    </div>
  </div>

  <!-- TEAM B PLAYERS -->
  <div class="card">
    <div class="team-header">
      <img src={$matchSetup.teamB.logo} class="team-logo-sm" alt="logo" />
      <div class="team-name" style="color:{$matchSetup.teamB.color}">{$matchSetup.teamB.name}</div>
    </div>
    {#if prefilledB}
      <div class="muted" style="font-size:11px; margin-bottom:6px; color:#86efac">✓ Last match's XI loaded — edit as needed</div>
    {/if}
    {#each $matchSetup.playersB as p, i (i)}
      <div class="player-row">
        <div class="num" style="display:flex; align-items:center; justify-content:center;">
          {#if i === 0}<Icon name="trophy" size={12} />{:else}{i + 1}{/if}
        </div>
        <input type="text" list="player-pool" bind:value={$matchSetup.playersB[i]} placeholder={i === 0 ? "Captain" : "Player " + (i + 1)} />
      </div>
    {/each}
    <div style="display:flex; gap: 8px; margin-top: 12px; flex-wrap: wrap;">
      <button class="btn btn-ghost" on:click={() => addPlayer('B')}>+ Add Player</button>
      <button class="btn btn-ghost" disabled={$matchSetup.playersB.length <= 2} on:click={() => removePlayer('B')}>- Remove</button>
      <button class="btn btn-ghost" style="color:#fbbf24; border-color:#fbbf24; display:flex; align-items:center; gap:6px;" on:click={() => openImportSearch('B')}>
        <Icon name="user" size={14} /> Import Profile
      </button>
      {#if prefilledB}
        <button class="btn btn-ghost" on:click={() => clearRoster('B')}>✕ Clear</button>
      {/if}
    </div>
  </div>

  <!-- OPENING PAIRS -->
  <div class="card">
    <div style="font-weight:800;margin-bottom:8px">Opening pair ({battingTeam.name})</div>
    
    <div class="field">
      <label for="opening-striker">Striker</label>
      <select id="opening-striker" bind:value={$matchSetup.striker}>
        <option value="">Select striker</option>
        {#each battingPool.filter(p => p !== $matchSetup.nonStriker) as o, i_each (o.id || o.name || i_each)}
          <option value={o}>{o}</option>
        {/each}
      </select>
    </div>

    <div class="field">
      <label for="opening-non-striker">Non-striker</label>
      <select id="opening-non-striker" bind:value={$matchSetup.nonStriker}>
        <option value="">Select non-striker</option>
        {#each battingPool.filter(p => p !== $matchSetup.striker) as o, i_each (o.id || o.name || i_each)}
          <option value={o}>{o}</option>
        {/each}
      </select>
    </div>

    <div class="field">
      <label for="opening-bowler">Opening bowler ({bowlingTeam.name})</label>
      <select id="opening-bowler" bind:value={$matchSetup.bowler}>
        <option value="">Select bowler</option>
        {#each bowlingPool as o, i_each (o.id || o.name || i_each)}
          <option value={o}>{o}</option>
        {/each}
      </select>
    </div>
  </div>

  <div class="card">
    <div style="font-weight:800;margin-bottom:8px">Phone Mode</div>
    <div class="field">
      <div id="device-mode-label" class="field-label">Device Mode</div>
      <div class="radio-group" role="radiogroup" aria-labelledby="device-mode-label" style="display:flex; flex-direction:column; gap:8px;">
        <label style="display:flex; align-items:center; gap:8px; font-weight:normal; background: rgba(0,0,0,0.2); padding: 8px; border-radius: 6px;">
          <input type="radio" bind:group={phoneMode} value="single_record" on:change={() => handlePhoneModeChange('single_record')} />
          <div>
            <strong>Single Phone (Score + Record)</strong>
            <div class="muted" style="font-size:11px">Use one phone to record video AND score (Landscape)</div>
          </div>
        </label>
        <label style="display:flex; align-items:center; gap:8px; font-weight:normal; background: rgba(0,0,0,0.2); padding: 8px; border-radius: 6px;">
          <input type="radio" bind:group={phoneMode} value="single_score" on:change={() => handlePhoneModeChange('single_score')} />
          <div>
            <strong>Single Phone (Score Only)</strong>
            <div class="muted" style="font-size:11px">Use one phone just to score the match (Portrait)</div>
          </div>
        </label>
        <label style="display:flex; align-items:center; gap:8px; font-weight:normal; background: rgba(0,0,0,0.2); padding: 8px; border-radius: 6px;">
          <input type="radio" bind:group={phoneMode} value="scorer" on:change={() => handlePhoneModeChange('scorer')} />
          <div>
            <strong>Dual Phone (Scorer)</strong>
            <div class="muted" style="font-size:11px">This phone will be used for scoring</div>
          </div>
        </label>
        <label style="display:flex; align-items:center; gap:8px; font-weight:normal; background: rgba(0,0,0,0.2); padding: 8px; border-radius: 6px;">
          <input type="radio" bind:group={phoneMode} value="recorder" on:change={() => handlePhoneModeChange('recorder')} />
          <div>
            <strong>Dual Phone (Recorder)</strong>
            <div class="muted" style="font-size:11px">This phone will be used for recording</div>
          </div>
        </label>
      </div>
    </div>
  </div>

  {#if startError}
    <div class="card" style="color:#fca5a5; border-color:#7f1d1d; font-size:12px">{startError}</div>
  {/if}
  <button class="btn btn-primary btn-block" disabled={isStarting} on:click={startMatch}>
    START MATCH ▶
  </button>
</div>

{#if $deviceCheckOpen}
  <DeviceCheckModal on:accept={handleDeviceCheckAccepted} on:close={handleDeviceCheckClosed} />
{/if}

{#if showSearchModal}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="modal-backdrop" on:click={() => showSearchModal = false}>
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div class="modal-content" on:click|stopPropagation>
      <h3 style="color:#fbbf24; margin-top:0; margin-bottom:12px; font-weight:900; display:flex; align-items:center; gap:6px;">
        <Icon name="user" size={20} /> Search & Import Player
      </h3>
      <p style="font-size:12px; color:#94a3b8; margin-bottom:16px; font-weight:600;">Type username (e.g. virat_king_18), email, or phone number</p>
      
      <div class="field" style="margin-bottom:15px;">
        <input type="text" bind:value={searchVal} placeholder="Username, Email or Phone" style="width:100%; padding:12px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-weight:800; font-size:15px;" on:keydown={(e) => e.key === 'Enter' && triggerSearch()} />
      </div>

      {#if isSearchingPlayer}
        <div style="text-align:center; padding:10px 0; color:#fbbf24; font-weight:800; display:flex; justify-content:center; align-items:center; gap:6px;">
          <Icon name="refresh" size={16} /> Searching player...
        </div>
      {/if}

      {#if searchError}
        <div style="color:#ef4444; font-size:12px; font-weight:800; margin-top:10px; text-align:center;">⚠️ {searchError}</div>
      {/if}

      {#if searchResult}
        <div style="margin-top:16px; background:#0f172a; padding:12px; border-radius:8px; border:1px solid #334155; display:flex; align-items:center; gap:12px;">
          {#if searchResult.photo}
            <img src={searchResult.photo} alt="Avatar" style="width:50px; height:50px; border-radius:50%; object-fit:cover; border:2px solid #fbbf24;" />
          {:else}
            <div style="width:50px; height:50px; border-radius:50%; background:#1e293b; display:flex; align-items:center; justify-content:center; border:2px solid #475569; color:#94a3b8;">
              <Icon name="user" size={24} />
            </div>
          {/if}
          <div style="flex:1;">
            <div style="font-weight:900; color:white; font-size:15px;">{searchResult.displayName}</div>
            <div style="font-size:12px; color:#94a3b8; font-weight:800;">@{searchResult.username}</div>
          </div>
          <button class="btn btn-primary" style="padding:6px 12px; font-size:12px; font-weight:800; margin:0;" on:click={addImportedPlayer}>Add</button>
        </div>
      {/if}

      <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:20px;">
        <button class="btn btn-ghost" style="padding:10px 16px; font-weight:800;" on:click={() => showSearchModal = false}>Cancel</button>
        <button class="btn btn-primary" style="padding:10px 16px; font-weight:800; margin:0;" on:click={triggerSearch} disabled={isSearchingPlayer}>Search</button>
      </div>
    </div>
  </div>
{/if}
